/**
 * Hook for fetching and transforming profit data for adaptive charts
 */

import { useState, useEffect } from 'react';
import { DateRange } from '../utils/dateUtils';
import { getHourlyProfit, getDailyProfitRange } from '../api/profit';
import {
  ChartDataPoint,
  transformHourlyToChartData,
  transformDailyToChartData,
  aggregateToWeekly,
  aggregateToMonthly,
  fillDailyGaps
} from '../utils/dataAggregation';
import { determineGranularity, ChartGranularity, ChartType } from '../utils/chartUtils';

/**
 * Chart data fetch result
 */
export interface ChartDataResult {
  data: ChartDataPoint[];
  granularity: ChartGranularity;
  chartType: ChartType;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch and transform profit data for adaptive chart
 * @param dateRange - Date range to fetch data for
 * @param refreshTrigger - Trigger to force refresh
 * @returns Chart data result
 */
export function useAdaptiveChartData(
  dateRange: DateRange,
  refreshTrigger: number = 0
): ChartDataResult {
  const [data, setData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determine granularity based on date range
  const config = determineGranularity(dateRange.start_date, dateRange.end_date);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (config.granularity === 'hourly') {
          // Fetch hourly data for single day
          const hourlyData = await getHourlyProfit(dateRange.start_date, undefined);
          const chartData = transformHourlyToChartData(hourlyData, dateRange.start_date);
          setData(chartData);
        } else {
          // Fetch daily data for date range
          const dailyData = await getDailyProfitRange(
            dateRange.start_date,
            dateRange.end_date,
            undefined
          );

          // Fill gaps in daily data
          const filledData = fillDailyGaps(dailyData, dateRange.start_date, dateRange.end_date);

          if (config.granularity === 'daily') {
            // Transform to chart data
            const chartData = transformDailyToChartData(filledData);
            setData(chartData);
          } else if (config.granularity === 'weekly') {
            // Aggregate to weekly
            const weeklyData = aggregateToWeekly(filledData);
            setData(weeklyData);
          } else if (config.granularity === 'monthly') {
            // Aggregate to monthly
            const monthlyData = aggregateToMonthly(filledData);
            setData(monthlyData);
          }
        }
      } catch (err) {
        console.error('Error fetching chart data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load chart data');
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRange.start_date, dateRange.end_date, config.granularity, refreshTrigger]);

  return {
    data,
    granularity: config.granularity,
    chartType: config.chartType,
    loading,
    error
  };
}
